/**
 * COMMON_ASSETS — 跨遊戲共用的 host UI 圖示。
 *
 * 「真正不屬於任何單一遊戲」的 icon / image 都放這裡（modal close 按鈕、
 * 規則頁日期 picker 圖、共用 (i) info icon 等）。
 *
 * 不要放：
 *   - 遊戲主題背景圖、wallet icon 之類「跟遊戲視覺殼綁定」的素材
 *     → 放 `src/games/<gameId>/assets.ts` 的 `common` / `chrome` 區
 *   - 賠付頁 / 規則頁 / 歷史頁的 game-specific 內容圖 → 同上
 *
 * 命名規則：key 描述「這張圖是幹嘛的」（close / info / calendar），
 * 不要寫成 `xxxIcon` 重複 — 整支 const 都是 icon。
 *
 * 路徑來源：
 *   - `staticImgFromAssets` (`r`)：build-time hash 引入 `src/assets/common/...`
 *   - `imgFromCloud` (`c`)：上 CDN 的 `images/common/...`
 */

import { staticImgFromAssets as r } from '@/composables/useUtils'

/**
 * 跨遊戲共用的「規則頁按鈕」alpha 形狀（spin/stop 以外都長一樣、只差系統色）。
 * 用法：規則頁 group block 的 item 設 `tint: true` → 渲染走 mask + `background-color: var(--color-theme)`，
 * 自動染成當款系統色。一套共用、不必每款各上一份；spin/stop 仍各款自帶原圖。
 * （目前 local `src/assets/common/rules/`；之後可上 CDN 改 `c('images/common/rules/...')`。）
 */
export const RULE_BUTTONS = {
  minus: r('common/rules/rule_minus.webp'),
  plus: r('common/rules/rule_plus.webp'),
  bet: r('common/rules/rule_bet.webp'),
  wallet: r('common/rules/rule_wallet.webp'),
  win: r('common/rules/rule_win.webp'),
  winEn: r('common/rules/rule_win_en.webp'),
  autoSpin: r('common/rules/rule_auto.webp'),
  autoSpinEn: r('common/rules/rule_auto_en.webp'),
  turbo: r('common/rules/rule_turbo.webp'),
  turboEn: r('common/rules/rule_turbo_en.webp'),
  sound: r('common/rules/rule_sound.webp'),
  paytable: r('common/rules/rule_paytable.webp'),
  rules: r('common/rules/rule_rules.webp'),
  history: r('common/rules/rule_history.webp'),
  historyEn: r('common/rules/rule_history_en.webp'),
} as const

/**
 * 跨遊戲共用的「歷史頁 UI icon」alpha 形狀（各款同形狀、只差系統色）。
 * 單色 icon（freeSpin / winCount / win）→ `<TintIcon>` 或 mask + `background-color: var(--color-theme)` 染系統色。
 * `next`（盤面上下局箭頭）是**固定灰圈 + 主題色箭頭**兩色 → 兩層：`circle` 原圖墊底 + `arrow` 走 tint。
 * （目前 local `src/assets/common/history/`；之後可上 CDN 改 `c('images/common/history/...')`。）
 */
export const HISTORY_ICONS = {
  freeSpin: r('common/history/history_freespin.webp'),
  winCount: r('common/history/history_wincount.webp'),
  win: r('common/history/history_win.webp'),
  next: {
    circle: r('common/history/history_next_circle.webp'), // 固定灰圈（不染）
    arrow: r('common/history/history_next_arrow.webp'), // 主題色箭頭（tint）
  },
  /**
   * **雙向任贏中獎線的方向標**（競品原圖，70×70、全透背景、白色**雙 chevron 原圖朝右** ⏩）。
   * 用於 `WinSection` payline 列最左（`PaylineRowVM` 的 `type: 'bothWays'` 列）。
   * 🛑 **`ltr` 才 `-scale-x-100` 翻成朝左、`rtl` 用原圖朝右** —— 競品的箭頭指向該線的**起算軸**
   *   （左起算指左 / 右起算指右），不是派彩行進方向（2026-09-08 依使用者對照競品修正）。
   * 🛑 **不 tint** —— 競品就是白的，染成主題色會走樣。
   */
  expand: r('common/history/expand.webp'),
} as const

export const COMMON_ASSETS = {
  /** Modal 右上角關閉鍵（PopupWrapper 互動用 X）。原雲端 images/common/3d4456cc38a4ca8b.png */
  close: r('common/ui/close.webp'),
  /**
   * 規則頁 group block 的「关闭」按鈕說明 icon（教學圖：示意「這顆按鈕長這樣」）。
   * 跟 `close` 是不同檔案：`close` 是 host 自己的 X；這張是遊戲內 close 按鈕的視覺
   * 代表，貼在規則頁 group block 的旁邊。原雲端 images/common/13ffafb1ef92e31e.png
   */
  iconClose: r('common/rules/iconClose.webp'),
  /** 規則頁 group block 的「更多设置」按鈕說明 icon。原雲端 images/common/e7409bb3bb6a3189.png */
  iconSettings: r('common/rules/iconSettings.webp'),
  /** WinSection / 任何「點擊看更多 / 公式」場景的 (i) icon。原雲端 images/common/6ab9065d8bf23d4f.png */
  info: r('common/history/info.webp'),
  /** 規則頁 history 段的內嵌 {calendar} placeholder、日期 picker 用。原雲端 images/common/f36498d59d9e9ee8.png */
  calendar: r('common/rules/calendar.webp'),
  /** 規則頁說明用「?」icon。原雲端 images/ANBS/526d701eeb7bfa8c.png */
  iconQuestion: r('common/paytable/iconQuestion.webp'),
  /** HistoryTable 每列右側「>」進入詳情的箭頭。原雲端 images/common/7889438a901e1fb1.png */
  more: r('common/history/more.webp'),
} as const

export type CommonAssets = typeof COMMON_ASSETS
